import React, { useState, useRef, useCallback } from "react";
import { createPortal } from "react-dom";

interface TooltipProps {
  text: string;
  children: React.ReactElement;
  delay?: number;
}

export const Tooltip: React.FC<TooltipProps> = ({ text, children, delay = 500 }) => {
  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const show = useCallback((e: React.MouseEvent) => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setPos({
      x: rect.left + rect.width / 2,
      y: rect.bottom + 8,
    });
    timerRef.current = setTimeout(() => setVisible(true), delay);
  }, [delay]);

  const hide = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setVisible(false);
  }, []);

  const child = React.cloneElement(children, {
    onMouseEnter: (e: React.MouseEvent) => {
      show(e);
      children.props.onMouseEnter?.(e);
    },
    onMouseLeave: (e: React.MouseEvent) => {
      hide();
      children.props.onMouseLeave?.(e);
    },
    onFocus: (e: React.FocusEvent) => {
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      setPos({ x: rect.left + rect.width / 2, y: rect.bottom + 8 });
      setVisible(true);
      children.props.onFocus?.(e);
    },
    onBlur: (e: React.FocusEvent) => {
      hide();
      children.props.onBlur?.(e);
    },
  });

  return (
    <>
      {child}
      {visible && text && createPortal(
        <div
          role="tooltip"
          style={{
            position: "fixed",
            top: pos.y,
            left: pos.x,
            transform: "translateX(-50%)",
            zIndex: "var(--z-tooltip)",
            pointerEvents: "none",
            background: "var(--surface-hover, #2c2c32)",
            border: "1px solid var(--border)",
            borderRadius: "6px",
            fontSize: "11px",
            fontWeight: 400,
            color: "var(--text-muted)",
            fontFamily: "inherit",
            padding: "5px 10px",
            whiteSpace: "nowrap",
            boxShadow: "0 4px 14px rgba(0,0,0,0.4)",
            opacity: visible ? 1 : 0,
            transition: "opacity 0.15s",
          }}
        >
          {text}
        </div>,
        document.body
      )}
    </>
  );
};
